import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useWindowSize } from '@app/hooks';
import { LeadCardPageBackButton } from '@app/pages/Leads/LeadCardPage';
import { LeadsLoadedCount } from '@app/pages/Leads/ui/components/LeadsLoadedCount';
import { LeadsViewModeSwitcher } from '@app/pages/Leads/ui/components/LeadsViewModeSwitcher';
import type { LeadsViewMode } from '@app/pages/Leads/ui/components/LeadsViewModeSwitcher';
import { SoundToggleButton } from '@app/pages/Leads/ui/components/SoundToggleButton';
import { FiltersAccordion } from '@app/shared/components';

import { useLeadsRegistryHeader } from '../hooks';

import { LeadsRegistryChatScopeFilter } from './components/LeadsRegistryChatScopeFilter';
import { LeadsRegistryDisplaySettings } from './components/LeadsRegistryDisplaySettings';
import { LeadsRegistryFiltersBar } from './components/LeadsRegistryFiltersBar';
import { LeadsRegistryMassActions } from './components/LeadsRegistryMassActions';
import { LeadsRegistrySearch } from './components/LeadsRegistrySearch';
import { LeadsWriteFirstModal } from './components/LeadsWriteFirstModal';

import type { LeadsRegistryFilterScope } from '../model/types';
import type { FC } from 'react';

import './LeadsRegistryHeader.scss';

interface LeadsRegistryHeaderProps {
  viewMode?: LeadsViewMode;
}

export const LeadsRegistryHeader: FC<LeadsRegistryHeaderProps> = ({
  viewMode = 'registry',
}) => {
  const navigate = useNavigate();
  const filterScope: LeadsRegistryFilterScope =
    viewMode === 'funnels' ? 'funnels' : 'registry';
  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= 600;
  const {
    incomingMessageSoundToggleHandler,
    isIncomingMessageSoundEnabled,
    loadBatchSize,
    loadBatchSizeChangeHandler,
    loadBatchSizeOptions,
    maxVisibleColumns,
    maxVisibleColumnsChangeHandler,
    maxVisibleColumnsOptions,
    loadedCount,
    totalCount,
  } = useLeadsRegistryHeader(filterScope);

  const isFunnels = filterScope === 'funnels';
  const [isWriteFirstOpen, setIsWriteFirstOpen] = useState(false);
  const openWriteFirstHandler = useCallback(() => {
    setIsWriteFirstOpen(true);
  }, []);
  const closeWriteFirstHandler = useCallback(() => {
    setIsWriteFirstOpen(false);
  }, []);
  const backHandler = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="LeadsRegistryHeader">
      <div className="LeadsRegistryHeader__top">
        <div className="LeadsRegistryHeader__title-block">
          <LeadCardPageBackButton onBack={backHandler} />
          <LeadsViewModeSwitcher value={viewMode} />
        </div>

        {!isMobile && (
          <div className="LeadsRegistryHeader__scope-filter">
            <LeadsRegistryChatScopeFilter filterScope={filterScope} />
          </div>
        )}

        <div className="LeadsRegistryHeader__top-actions">
          <SoundToggleButton
            isSoundEnabled={isIncomingMessageSoundEnabled}
            onToggleSound={incomingMessageSoundToggleHandler}
          />
          <LeadsRegistryDisplaySettings
            loadBatchSize={loadBatchSize}
            loadBatchSizeOptions={loadBatchSizeOptions}
            onLoadBatchSizeChange={loadBatchSizeChangeHandler}
            {...(isFunnels && {
              maxVisibleColumns,
              maxVisibleColumnsOptions,
              onMaxVisibleColumnsChange: maxVisibleColumnsChangeHandler,
            })}
          />
        </div>
      </div>

      <div className="LeadsRegistryHeader__filters">
        <FiltersAccordion breakpoint="tablet">
          <div className="LeadsRegistryHeader__filters-panel">
            {isMobile && (
              <LeadsRegistryChatScopeFilter filterScope={filterScope} />
            )}
            <LeadsRegistrySearch filterScope={filterScope} />
            <LeadsRegistryFiltersBar filterScope={filterScope} />
          </div>
        </FiltersAccordion>
      </div>

      <div className="LeadsRegistryHeader__bottom">
        <LeadsLoadedCount loadedCount={loadedCount} totalCount={totalCount} />
        <div className="LeadsRegistryHeader__bottom-actions">
          <LeadsRegistryMassActions />
          <Button
            className="LeadsRegistryHeader__write-first"
            size="small"
            icon={<EditOutlined />}
            onClick={openWriteFirstHandler}
          >
            Написать первым
          </Button>
        </div>
      </div>

      <LeadsWriteFirstModal
        open={isWriteFirstOpen}
        onClose={closeWriteFirstHandler}
      />
    </div>
  );
};
